import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of } from 'rxjs';
import {
  ApplicationStatusEnum,
  BriefApplicationResponseDto,
} from '@/app/common/general-types/briefs.dto';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { SubmittedPostDto } from '@/app/common/general-types/submitted-posts.dto';
import { VideoResponseDto } from '@/app/common/general-types/videos.dto';
import { CreatorService } from '@/app/services/creator/creator.service';
import { SubmittedPostsService } from '@/app/services/submitted-posts/submitted-posts.service';
import { VideoService } from '@/app/services/video/video.service';
import {
  applicationNeedsActionData,
  createEmptySubmittablePosts,
  filterApprovedVideos,
  mergeSubmittedPostsIntoMap,
  resolveCreatorApplicationActions,
} from './application-actions.helpers';
import { SubmittablePostsMap } from './application-actions.types';
import { CreatorPostOnPlatform } from '../post-on-platform/post-on-platform';
import { CreatorSubmitContent } from '../submit-content/submit-content';
import { CreatorUploadInstructions } from '../upload-instructions/upload-instructions';

@Component({
  selector: 'ins-creator-application-actions',
  imports: [CreatorSubmitContent, CreatorUploadInstructions, CreatorPostOnPlatform],
  templateUrl: './application-actions.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block space-y-3' },
})
export class CreatorApplicationActions {
  application = input.required<BriefApplicationResponseDto>();
  actionLoading = input(false);
  apply = output<void>();

  readonly creator = signal<CreatorResponseDto | undefined>(undefined);
  readonly approvedVideos = signal<VideoResponseDto[] | undefined>(undefined);
  readonly submittedPosts = signal<SubmittedPostDto[]>([]);
  readonly submittablePosts = signal<SubmittablePostsMap>({});
  readonly contextLoading = signal(false);

  readonly actions = computed(() =>
    resolveCreatorApplicationActions({
      application: this.application(),
      creatorLoaded: this.creator() !== undefined,
      approvedVideosLoaded: this.approvedVideos() !== undefined,
    }),
  );

  readonly ApplicationStatusEnum = ApplicationStatusEnum;

  private destroyRef = inject(DestroyRef);
  private creatorService = inject(CreatorService);
  private videoService = inject(VideoService);
  private submittedPostsService = inject(SubmittedPostsService);
  private loadedContextKey: string | undefined;

  constructor() {
    effect(() => {
      const app = this.application();
      if (!app?.id) {
        this.loadedContextKey = undefined;
        return;
      }

      const contextKey = `${app.id}:${app.applicationStatus}`;
      if (contextKey === this.loadedContextKey) {
        return;
      }

      this.loadedContextKey = contextKey;
      this.loadActionContext(app);
    });
  }

  onSubmittablePostsChange(next: SubmittablePostsMap): void {
    this.submittablePosts.set(next);
  }

  onSubmittedPostsChange(next: SubmittedPostDto[]): void {
    this.submittedPosts.set(next);
  }

  showAction(actionId: string, platform?: number): boolean {
    return this.actions().some(
      (action) => action.id === actionId && action.platform === platform,
    );
  }

  crossPostPlatforms(): number[] {
    return this.actions()
      .filter((action) => action.id === 'cross-post' && action.platform !== undefined)
      .map((action) => action.platform!);
  }

  private loadActionContext(app: BriefApplicationResponseDto): void {
    if (!applicationNeedsActionData(app)) {
      this.creator.set(undefined);
      this.approvedVideos.set([]);
      this.submittedPosts.set([]);
      this.submittablePosts.set({});
      return;
    }

    this.contextLoading.set(true);

    forkJoin({
      creator: this.creatorService.getCreatorById(app.creatorUserId).pipe(
        catchError(() => of(undefined as CreatorResponseDto | undefined)),
      ),
      videos: this.videoService.getCreatorBriefVideos(app.creatorUserId, app.briefId).pipe(
        catchError(() => of([] as VideoResponseDto[])),
      ),
      submittedPosts: this.submittedPostsService
        .getSubmittedPostsForBriefApplication(app.id)
        .pipe(catchError(() => of([] as SubmittedPostDto[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ creator, videos, submittedPosts }) => {
        const approved = filterApprovedVideos(videos);
        const primaryPlatform = app.brief?.shoutBrief?.socialPlatform;
        this.creator.set(creator);
        this.approvedVideos.set(approved);
        this.submittedPosts.set(submittedPosts);
        this.submittablePosts.set(
          mergeSubmittedPostsIntoMap(
            createEmptySubmittablePosts(approved),
            submittedPosts,
            primaryPlatform,
          ),
        );
        this.contextLoading.set(false);
      });
  }
}
